import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Briefcase, Cpu, BarChart3, HelpCircle, ChevronDown, User, X } from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';

const ICON_MAP = { Briefcase, Cpu, BarChart3 };
const NAV_ITEMS = [
  { id: 'open-jobs', label: 'Open jobs', icon: 'Briefcase', path: '/open-jobs' },
  { id: 'ai-interviewer', label: 'AI Interviewer', icon: 'Cpu', path: '/ai-interviewer' },
  { id: 'analytics', label: 'Analytics', icon: 'BarChart3', path: '/analytics' },
];

function Logo({ onClose }) {
  return (
    <div className="px-6 pt-6 pb-8 flex items-center justify-between">
      <div className="flex items-baseline">
        <span className="text-[24px] font-extrabold tracking-tight text-[#0A3900]">Duty AI</span>
      </div>
      {onClose && (
        <button
          onClick={onClose}
          className="md:hidden w-8 h-8 rounded-lg hover:bg-white/60 flex items-center justify-center text-[#4A4463]"
          aria-label="Close menu"
        >
          <X size={18} />
        </button>
      )}
    </div>
  );
}

function NavItem({ item, active, onClick }) {
  const Icon = ICON_MAP[item.icon];
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-[14.5px] transition-colors ${
        active
          ? 'bg-white shadow-sm text-[#1A1533] font-semibold'
          : 'text-[#4A4463] hover:bg-white/70 font-medium'
      }`}
    >
      <Icon size={18} strokeWidth={2} className={active ? 'text-[#5B4FE8]' : 'text-[#6B6485]'} />
      <span>{item.label}</span>
    </button>
  );
}

function UserFooter() {
  return (
    <div className="px-3 pb-4">
      <button className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-[#4A4463] hover:bg-white/70 text-[14px] font-medium transition-colors">
        <HelpCircle size={16} />
        <span>Got a question?</span>
      </button>
      <div className="mt-2 flex items-center gap-2 px-3 py-2.5 rounded-xl bg-white shadow-sm">
        <div className="w-8 h-8 rounded-full bg-[#0A3900] flex items-center justify-center text-white shrink-0">
          <User size={16} />
        </div>
        <span className="text-[14px] font-semibold text-[#1A1533] truncate flex-1">Recruiter</span>
        <ChevronDown size={16} className="text-[#6B6485] shrink-0" />
      </div>
    </div>
  );
}

function SidebarContent({ onClose, onNavigate, currentPath }) {
  return (
    <div className="flex flex-col h-full">
      <Logo onClose={onClose} />
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto zara-scroll">
        {NAV_ITEMS.map((item) => (
          <NavItem
            key={item.id}
            item={item}
            active={currentPath === item.path}
            onClick={() => onNavigate(item.path)}
          />
        ))}
      </nav>
      <UserFooter />
    </div>
  );
}

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mobileOpen, setMobileOpen } = useSidebar();
  const go = (path) => { navigate(path); setMobileOpen(false); };

  return (
    <>
      <aside className="hidden md:flex md:flex-col w-[240px] shrink-0 bg-[#F1ECFE] border-r border-[#E5DEF9] h-screen sticky top-0">
        <SidebarContent onNavigate={go} currentPath={location.pathname} />
      </aside>

      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-[#1A1533]/40" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[260px] bg-[#F1ECFE] shadow-xl">
            <SidebarContent
              onClose={() => setMobileOpen(false)}
              onNavigate={go}
              currentPath={location.pathname}
            />
          </aside>
        </div>
      )}
    </>
  );
}
